import styles from "../../../../styles/pages/Lead/LeadManagement/OpenDeals.module.css";
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useOutletContext, useParams } from "react-router-dom";
import { fetchLeadOrders } from "../../../../utils/api/Lead/OpenDeals/fetchLeadOrdersApi";
import { AddOrderModal } from "../../../../components/Modal/Open-deals/AddOrderModal";
import { EditOrderModal } from "../../../../components/Modal/Open-deals/EditOrderModal";
import { handleError } from "../../../../utils/custom/errorHandler";
import { formatDateTime } from "../../../../utils/custom/formatters";
import { LangContext } from "../../../../context/LangContext";
import { ThemeContext } from "../../../../context/ThemeContext";

const translations = {
    ru: {
        loading: "Загрузка данных...",
        loadingError: "Ошибка загрузки заказов...",
        addDeal: "Добавить сделку",
        noDeals: "Закрытых сделок не найдено.",
        adminData: "Данные администратора",
        adminId: "ID",
        adminFirstName: "Имя",
        adminLastName: "Фамилия",
        adminEmail: "Email",
        adminRole: "Роль",
        adminStatus: "Статус",
        adminCreated: "Создан",
        previousPage: "Назад",
        nextPage: "Вперед",
        tableHeaders: {
            id: "ID",
            type: "Тип",
            assetsId: "Актив",
            leverage: "Плечо",
            openPrice: "Цена открытия",
            takeProfit: "Take Profit",
            stopLoss: "Stop Loss",
            closePrice: "Цена закрытия",
            volume: "Объем",
            margin: "Маржа",
            profit: "Прибыль",
            closedAt: "Закрыто в",
            createdByAdmin: "Создано админом",
            createdAt: "Создано в",
        }
    },
    en: {
        loading: "Loading data...",
        loadingError: "Error loading orders...",
        addDeal: "Add deal",
        noDeals: "No closed deals found.",
        adminData: "Administrator Data",
        adminId: "ID",
        adminFirstName: "First Name",
        adminLastName: "Last Name",
        adminEmail: "Email",
        adminRole: "Role",
        adminStatus: "Status",
        adminCreated: "Created",
        previousPage: "Previous",
        nextPage: "Next",
        tableHeaders: {
            id: "ID",
            type: "Type",
            assetsId: "Asset",
            leverage: "Leverage",
            openPrice: "Open Price",
            takeProfit: "Take Profit",
            stopLoss: "Stop Loss",
            closePrice: "Close Price",
            volume: "Volume",
            margin: "Margin",
            profit: "Profit",
            closedAt: "Closed At",
            createdByAdmin: "Created by Admin",
            createdAt: "Created At",
        }
    },
};

export const TradeHistoryPage = () => {
    const { id: leadId } = useParams();
    const { refreshPanelData } = useOutletContext();
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hoveredAdmin, setHoveredAdmin] = useState(null);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [isAddOrderModalOpen, setIsAddOrderModalOpen] = useState(false);
    const [shouldUpdateOrders, setShouldUpdateOrders] = useState(false);
    const [isEditOrderModalOpen, setIsEditOrderModalOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(20);
    const [totalPages, setTotalPages] = useState(1);

    const loadOrders = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data, meta } = await fetchLeadOrders(leadId, 'closed', currentPage, perPage);
            setOrders(data ?? []);
            setTotalPages(meta.total_pages);
        } catch (err) {
            handleError(err.response?.data || err, setError);
        } finally {
            setLoading(false);
        }
    }, [leadId, currentPage, perPage]);

    useEffect(() => {
        loadOrders();
        if (shouldUpdateOrders) {
            setShouldUpdateOrders(false);
        }
    }, [loadOrders, shouldUpdateOrders]);

    const handleMouseEnter = (event, adminData) => {
        setHoveredAdmin(adminData);
        setModalPosition({ x: event.clientX - 100, y: event.clientY + 15 });
    };

    const handleMouseLeave = () => {
        setHoveredAdmin(null);
    };

    const handleOrderCreated = () => {
        setIsAddOrderModalOpen(false);
        setShouldUpdateOrders(true);
        setCurrentPage(1);
    };

    const handleEditOrderClick = (orderId) => {
        setSelectedOrderId(orderId);
        setIsEditOrderModalOpen(true);
    };

    const handleOrderActionSuccess = () => {
        setIsEditOrderModalOpen(false);
        setShouldUpdateOrders(true);
        refreshPanelData();
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const renderPaginationButtons = () => {
        const pages = [];
        const maxPageButtons = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

        if (endPage - startPage + 1 < maxPageButtons) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`${styles.paginationButton} ${i === currentPage ? styles.active : ''} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                    onClick={() => handlePageChange(i)}
                    disabled={loading}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    const containerClass = `${styles.container} ${theme === 'dark' ? styles.darkContainer : ''}`;
    const tableClass = `${styles.table} ${theme === 'dark' ? styles.darkTable : ''}`;
    const adminModalClass = `${styles.adminModal} ${theme === 'dark' ? styles.darkAdminModal : ''}`;

    if (loading) {
        return <div className={`${styles.loadingContainer} ${theme === 'dark' ? styles.darkText : ''}`}>{currentTranslation.loading}</div>;
    }

    if (error) {
        return (
            <div className={`${styles.errorMessage} ${theme === 'dark' ? styles.darkErrorMessage : ''}`}>
                <p>{currentTranslation.loadingError}</p>
                <p>{error.general}</p>
            </div>
        )
    }

    return (
        <div className={containerClass}>
            <div className={styles.topBar}>
                <div className={styles.addButtonContainer}>
                    <button className={`${styles.addButton} ${theme === 'dark' ? styles.darkAddButton : ''}`} onClick={() => setIsAddOrderModalOpen(true)}>
                        <p className={styles.addButtonText}>{currentTranslation.addDeal}</p>
                    </button>
                </div>
            </div>

            <table className={tableClass}>
                <thead>
                    <tr>
                        <th>{currentTranslation.tableHeaders.id}</th>
                        <th>{currentTranslation.tableHeaders.type}</th>
                        <th>{currentTranslation.tableHeaders.assetsId}</th>
                        <th>{currentTranslation.tableHeaders.leverage}</th>
                        <th>{currentTranslation.tableHeaders.openPrice}</th>
                        <th>{currentTranslation.tableHeaders.takeProfit}</th>
                        <th>{currentTranslation.tableHeaders.stopLoss}</th>
                        <th>{currentTranslation.tableHeaders.closePrice}</th>
                        <th>{currentTranslation.tableHeaders.volume}</th>
                        <th>{currentTranslation.tableHeaders.margin}</th>
                        <th>{currentTranslation.tableHeaders.profit}</th>
                        <th>{currentTranslation.tableHeaders.closedAt}</th>
                        <th>{currentTranslation.tableHeaders.createdByAdmin}</th>
                        <th>{currentTranslation.tableHeaders.createdAt}</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.length === 0 ? (
                        <tr>
                            <td colSpan="14" className={`${styles.noOrdersMessage} ${theme === 'dark' ? styles.darkText : ''}`}>
                                {currentTranslation.noDeals}
                            </td>
                        </tr>
                    ) : (
                        orders.map((order) => (
                            <tr key={order.id}>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.id}>
                                    <span
                                        className={`${styles.orderIdLink} ${theme === 'dark' ? styles.darkIdLink : ''}`}
                                        onClick={() => handleEditOrderClick(order.id)}
                                    >
                                        {order.id}
                                    </span>
                                </td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.type}>{order.type}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.assetsId}>{order.asset.name}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.leverage}>{order.leverage}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.openPrice}>{order.open_price}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.takeProfit}>{order.take_profit_price || '-'}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.stopLoss}>{order.stop_loss_price || '-'}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.closePrice}>{order.close_price || '-'}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.volume}>{order.volume}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.margin}>{order.margin}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.profit}>{order.profit}</td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.closedAt}>{formatDateTime(order.closed_at)}</td>
                                <td
                                    className={styles.tableCell}
                                    data-label={currentTranslation.tableHeaders.createdByAdmin}
                                    onMouseEnter={(e) => handleMouseEnter(e, order.created_by_admin)}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    {order.created_by_admin ? `${order.created_by_admin.email}` : '-'}
                                </td>
                                <td className={styles.tableCell} data-label={currentTranslation.tableHeaders.createdAt}>{formatDateTime(order.created_at)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        type="button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className={`${styles.paginationButton} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                    >
                        {currentTranslation.previousPage}
                    </button>
                    {renderPaginationButtons()}
                    <button
                        type="button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || loading}
                        className={`${styles.paginationButton} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                    >
                        {currentTranslation.nextPage}
                    </button>
                </div>
            )}

            {hoveredAdmin && (
                <div
                    className={adminModalClass}
                    style={{ left: modalPosition.x, top: modalPosition.y }}
                >
                    <h4>{currentTranslation.adminData}</h4>
                    <p><strong>{currentTranslation.adminId}:</strong> {hoveredAdmin.id}</p>
                    <p><strong>{currentTranslation.adminFirstName}:</strong> {hoveredAdmin.first_name || '-'}</p>
                    <p><strong>{currentTranslation.adminLastName}:</strong> {hoveredAdmin.last_name || '-'}</p>
                    <p><strong>{currentTranslation.adminEmail}:</strong> {hoveredAdmin.email}</p>
                    <p><strong>{currentTranslation.adminRole}:</strong> {hoveredAdmin.role?.name || '-'}</p>
                    <p><strong>{currentTranslation.adminStatus}:</strong> {hoveredAdmin.status}</p>
                    <p><strong>{currentTranslation.adminCreated}:</strong> {formatDateTime(hoveredAdmin.created_at)}</p>
                </div>
            )}

            {isAddOrderModalOpen && (
                <AddOrderModal
                    leadId={leadId}
                    onClose={() => setIsAddOrderModalOpen(false)}
                    onOrderCreated={handleOrderCreated}
                    theme={theme}
                />
            )}

            {isEditOrderModalOpen && selectedOrderId && (
                <EditOrderModal
                    orderId={selectedOrderId}
                    onClose={() => setIsEditOrderModalOpen(false)}
                    onOrderActionSuccess={handleOrderActionSuccess}
                    theme={theme}
                />
            )}
        </div>
    );
};
